import api from './api';
import { resolveSampleDataSource } from './systems/sample-local/sampleLocalMode';
import { sampleLocalStore } from './systems/sample-local/sampleLocalStore';
import {
  buildLocalReportTablePage,
  buildLocalUsageLedgerData,
} from './systems/sample-local/sampleLocalUsageLedger';
import {
  coverPrefsToApiOptions,
  loadUsageLedgerPdfCoverPrefs,
} from './systems/sample-local/usageLedgerPdfCoverPrefs';
import { USAGE_LEDGER_INVENTORY_VIEW } from '../constants/usageLedgerInventory';

export interface ReportSample {
  id: number;
  name: string;
  sampleType?: string | null;
  bslLevel?: string | null;
  infectiousDiseaseLaw?: string | null;
  initialQuantity?: number | null;
  remainingQuantity?: number | null;
  usedQuantity?: number | null;
  unit?: string | null;
  storageDate?: string | null;
  expirationDate?: string | null;
  notes?: string | null;
  createdAt: string;
  user?: {
    id: number;
    username: string;
    firstName: string;
    lastName: string;
    organizationName?: string | null;
  };
  storageRoom?: {
    id: number;
    name: string;
    facility?: {
      id: number;
      organization: string;
      laboratory: string;
    };
  } | null;
  usageRecords?: Array<{
    id: number;
    quantity: number;
    usageDate: string;
  }>;
  hierarchy?: {
    creator?: string;
    facility?: string;
    laboratory?: string;
    storageRoom?: string;
    storageRoomLocation?: string | null;
    drawerNumber?: number | null;
    caseName?: string | null;
    bslLevel?: string;
    pathogenGenus?: string;
    pathogenSpecies?: string;
    pathogenSubspecies?: string;
    pathogenStrain?: string;
    type?: string;
    sampleName?: string;
  };
}

export interface ReportStatistics {
  totalSamples: number;
  availableSamples?: number;
  outOfStockSamples?: number;
  byBslLevel: Array<{ level: string; count: number }>;
  bySampleType: Array<{ type: string; count: number }>;
  byFacility: Array<{
    facility: string;
    storageRoom: string;
    count: number;
  }>;
  monthlyRegistration: Array<{ month: string; count: number }>;
}

export interface ReportFilters {
  creator?: string;
  location?: string;
  facility?: string;
  laboratory?: string;
  storageRoom?: string;
  drawerNumber?: string;
  caseName?: string;
  bslLevel?: string;
  sampleType?: string;
  sampleName?: string;
  notes?: string;
  notesIsEmpty?: boolean;
  pathogenGenus?: string;
  pathogenSpecies?: string;
  pathogenSubspecies?: string;
  pathogenStrain?: string;
  keyword?: string;
  startDate?: string;
  endDate?: string;
}

export interface ReportResponse {
  success: boolean;
  data: ReportSample[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  statistics: ReportStatistics;
}

/** /reports DataTables 列 index とソート方向（serverSide の order と対応） */
export interface ReportSortOrder {
  column: number;
  dir: 'asc' | 'desc';
}

export interface UsageRecord {
  id: number;
  sampleId: number;
  userId: number;
  quantity: number;
  usageDate: string;
  purpose?: string | null;
  notes?: string | null;
  createdAt: string;
  sample?: {
    id: number;
    name: string;
    sampleType?: string | null;
  };
  user?: {
    id: number;
    username: string;
    firstName: string;
    lastName: string;
  };
}

export interface UsageReportResponse {
  success: boolean;
  data: UsageRecord[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: {
    totalUsage: number;
    totalRecords: number;
  };
}

export const reportService = {
  async getReportData(params?: {
    baseDate?: string;
    page?: number;
    limit?: number;
    filters?: ReportFilters;
    hierarchy?: string[];
    sortOrders?: ReportSortOrder[];
    view?: string;
    /** positive（既定）= 残>0 / zero = 残0 / all = 両方 */
    stockFilter?: 'positive' | 'zero' | 'all';
    adminViewAll?: boolean;
  }): Promise<ReportResponse> {
    if (resolveSampleDataSource() === 'local') {
      const page = buildLocalReportTablePage({
        baseDate: params?.baseDate,
        page: params?.page,
        limit: params?.limit,
        stockFilter: params?.stockFilter,
        filters: params?.filters,
      });
      return {
        success: true,
        data: page.data as ReportSample[],
        pagination: page.pagination,
        statistics: page.statistics,
      };
    }
    const filterParams = buildReportFilterParams(params?.filters);
    const response = await api.get('/reports/samples', {
      params: {
        baseDate: params?.baseDate,
        page: params?.page,
        limit: params?.limit,
        hierarchy: params?.hierarchy,
        view: params?.view ?? USAGE_LEDGER_INVENTORY_VIEW,
        stockFilter: params?.stockFilter ?? 'positive',
        ...(params?.sortOrders?.length
          ? { reportSort: JSON.stringify(params.sortOrders) }
          : {}),
        ...(params?.adminViewAll ? { adminViewAll: 'true' } : {}),
        ...filterParams,
      },
      timeout: 60000, // 60秒
    });
    return response.data;
  },

  async getUsageReport(params?: {
    startDate?: string;
    endDate?: string;
    sampleId?: number;
    userId?: number;
    page?: number;
    limit?: number;
  }): Promise<UsageReportResponse> {
    if (resolveSampleDataSource() === 'local') {
      throw new Error('sample.export_requires_db_mode');
    }
    const response = await api.get('/reports/usage', { params });
    return response.data;
  },

  async getDistributionAnalysis(options?: { adminViewAll?: boolean }): Promise<{
    success: boolean;
    data: {
      byPathogenGenus: Array<{ genus: string; count: number }>;
      byPathogenSpecies: Array<{ species: string; count: number }>;
      byBslLevel: Array<{ level: string; count: number; totalQuantity: number }>;
    };
  }> {
    if (resolveSampleDataSource() === 'local') {
      const snap = sampleLocalStore.getSnapshot();
      const byGenus = new Map<string, number>();
      const bySpecies = new Map<string, number>();
      const byBsl = new Map<string, { count: number; totalQuantity: number }>();
      for (const s of snap.samples) {
        const g = s.pathogenGenus || '-';
        const sp = s.pathogenSpecies || '-';
        const bsl = s.bslLevel || '-';
        byGenus.set(g, (byGenus.get(g) || 0) + 1);
        bySpecies.set(sp, (bySpecies.get(sp) || 0) + 1);
        const cur = byBsl.get(bsl) || { count: 0, totalQuantity: 0 };
        cur.count += 1;
        cur.totalQuantity += s.remainingQuantity ?? 0;
        byBsl.set(bsl, cur);
      }
      return {
        success: true,
        data: {
          byPathogenGenus: [...byGenus.entries()].map(([genus, count]) => ({ genus, count })),
          byPathogenSpecies: [...bySpecies.entries()].map(([species, count]) => ({ species, count })),
          byBslLevel: [...byBsl.entries()].map(([level, v]) => ({
            level,
            count: v.count,
            totalQuantity: v.totalQuantity,
          })),
        },
      };
    }
    const response = await api.get('/reports/distribution', {
      params: {
        adminViewAll: options?.adminViewAll ? 'true' : undefined,
      },
    });
    return response.data;
  },

  async exportUsageLedgerPdf(params: {
    baseDate?: string;
    filters?: ReportFilters;
    adminViewAll?: boolean;
  }): Promise<Blob> {
    const coverPrefs = loadUsageLedgerPdfCoverPrefs();
    const coverOptions = coverPrefsToApiOptions(coverPrefs);
    const issuerOverride = {
      organizationName: coverPrefs.organizationName.trim() || undefined,
      departmentName: coverPrefs.departmentName.trim() || undefined,
      preparedBy: coverPrefs.preparedBy.trim() || undefined,
    };

    if (resolveSampleDataSource() === 'local') {
      const ledger = buildLocalUsageLedgerData({
        baseDate: params.baseDate,
        filters: params.filters,
      });
      const response = await api.post(
        '/reports/usage-ledger-pdf',
        {
          baseDate: ledger.baseDate,
          periodStart: ledger.periodStart,
          samples: ledger.samples,
          coverOptions,
          issuer: issuerOverride,
        },
        {
          responseType: 'blob',
          timeout: 300000,
        }
      );
      return response.data;
    }

    const filterParams = buildReportFilterParams(params.filters);
    const response = await api.get('/reports/usage-ledger-pdf', {
      params: {
        baseDate: params.baseDate,
        ...filterParams,
        ...(params.adminViewAll ? { adminViewAll: 'true' } : {}),
        cover: JSON.stringify(coverOptions),
        ...(issuerOverride.organizationName
          ? { issuerOrg: issuerOverride.organizationName }
          : {}),
        ...(issuerOverride.departmentName
          ? { issuerDept: issuerOverride.departmentName }
          : {}),
        ...(issuerOverride.preparedBy ? { issuerBy: issuerOverride.preparedBy } : {}),
      },
      responseType: 'blob',
      timeout: 300000,
    });
    return response.data;
  },

  async getUsageRecordsByDate(params: {
    baseDate?: string;
    filters?: ReportFilters;
    adminViewAll?: boolean;
  }): Promise<UsageRecordsByDateData> {
    if (resolveSampleDataSource() === 'local') {
      return sampleLocalStore.getUsageRecordsByDate({
        baseDate: params.baseDate,
        filters: { keyword: params.filters?.keyword },
      });
    }
    const filterParams = buildReportFilterParams(params.filters);
    const response = await api.get('/reports/usage-records', {
      params: {
        baseDate: params.baseDate,
        ...filterParams,
        ...(params.adminViewAll ? { adminViewAll: 'true' } : {}),
      },
    });
    return response.data.data;
  },

  async exportUsageRecordsPdf(params: {
    baseDate?: string;
    filters?: ReportFilters;
    adminViewAll?: boolean;
  }): Promise<Blob> {
    if (resolveSampleDataSource() === 'local') {
      throw new Error('sample.export_requires_db_mode');
    }
    const filterParams = buildReportFilterParams(params.filters);
    const response = await api.get('/reports/usage-records-pdf', {
      params: {
        baseDate: params.baseDate,
        ...filterParams,
        ...(params.adminViewAll ? { adminViewAll: 'true' } : {}),
      },
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data;
  },

  async exportUsageRecordsExcel(params: {
    baseDate?: string;
    filters?: ReportFilters;
    adminViewAll?: boolean;
  }): Promise<Blob> {
    if (resolveSampleDataSource() === 'local') {
      throw new Error('sample.export_requires_db_mode');
    }
    const filterParams = buildReportFilterParams(params.filters);
    const response = await api.get('/reports/usage-records-excel', {
      params: {
        baseDate: params.baseDate,
        ...filterParams,
        ...(params.adminViewAll ? { adminViewAll: 'true' } : {}),
      },
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data;
  },
};

function buildReportFilterParams(filters?: ReportFilters): Record<string, string> {
  if (!filters) return {};
  return Object.fromEntries(
    Object.entries(filters)
      .filter(([, value]) => {
        if (value === undefined || value === null) return false;
        if (typeof value === 'string' && value.trim() === '') return false;
        if (value === false) return false;
        return true;
      })
      .map(([key, value]) => [key, typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value)])
  );
}

export interface UsageRecordDayRow {
  id: number;
  sampleId: number;
  sampleName: string;
  sampleType: string | null;
  bslLevel: string | null;
  unit?: string | null;
  storageRoomName: string | null;
  storageRoomLocation: string | null;
  drawerNumber: number | null;
  caseName: string | null;
  creatorName: string | null;
  action: string | null;
  quantity: number;
  remainingQuantity: number | null;
  purpose: string | null;
  notes: string | null;
  recordedBy: string | null;
  usageDate: string;
}

export interface UsageRecordDayGroup {
  date: string;
  records: UsageRecordDayRow[];
}

export interface UsageRecordsByDateData {
  baseDate: string;
  periodStart: string;
  periodEnd: string;
  days: UsageRecordDayGroup[];
  totalRecords: number;
}
